/**
 * EmailPreview component - preview marketing email with sample data
 */

import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

interface EmailPreviewProps {
  subject: string;
  htmlContent: string;
  sampleData?: {
    customer_name: string;
    vehicle_make: string;
    vehicle_model: string;
    vehicle_registration: string;
    booking_link: string;
  };
}

export function EmailPreview({ subject, htmlContent, sampleData }: EmailPreviewProps) {
  const defaultSampleData = {
    customer_name: 'Juan García',
    vehicle_make: 'Volkswagen',
    vehicle_model: 'Golf',
    vehicle_registration: '1234 ABC',
    booking_link: 'https://example.com/booking',
  };

  const data = sampleData || defaultSampleData;

  const personalizeContent = (content: string) => {
    return content
      .replace(/\{\{customer_name\}\}/g, data.customer_name)
      .replace(/\{\{vehicle_make\}\}/g, data.vehicle_make)
      .replace(/\{\{vehicle_model\}\}/g, data.vehicle_model)
      .replace(/\{\{vehicle_registration\}\}/g, data.vehicle_registration)
      .replace(/\{\{booking_link\}\}/g, data.booking_link);
  };

  const personalizedSubject = personalizeContent(subject);
  const personalizedHtml = personalizeContent(htmlContent);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Vista previa del email</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="p-3 bg-muted rounded-md">
            <span className="text-sm text-muted-foreground">Asunto:</span>
            <p className="font-medium">{personalizedSubject}</p>
          </div>

          <Tabs defaultValue="preview" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="preview">Vista previa</TabsTrigger>
              <TabsTrigger value="code">Código HTML</TabsTrigger>
            </TabsList>
            <TabsContent value="preview" className="mt-4">
              <div
                className="border rounded-md overflow-hidden"
                style={{ maxHeight: '500px', overflowY: 'auto' }}
              >
                <iframe
                  srcDoc={personalizedHtml}
                  title="Email preview"
                  className="w-full h-[500px] border-0"
                  sandbox="allow-same-origin"
                />
              </div>
            </TabsContent>
            <TabsContent value="code" className="mt-4">
              <pre className="p-4 bg-muted rounded-md overflow-auto text-xs max-h-[500px]">
                {personalizedHtml}
              </pre>
            </TabsContent>
          </Tabs>

          <div className="text-xs text-muted-foreground">
            <p className="font-medium mb-1">Variables disponibles:</p>
            <code className="text-xs">
              {'{{customer_name}}'}, {'{{vehicle_make}}'}, {'{{vehicle_model}}'},{' '}
              {'{{vehicle_registration}}'}, {'{{booking_link}}'}
            </code>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
